<h2 class="heading-5">Tell us what's wrong</h2>

@if (vm.isFreeTextSearch) {
  <p class="body mt-8">
    Type a word or two about what’s wrong with your
    {{ vm.productDescription | lowercase }}. Or enter the error code.
  </p>
  <p class="body mt-8 mb-16 sm-mb-12">
    If your fault isn’t listed, please pick the most similar. Or if you have more than one fault, pick the main one.
  </p>

  <p class="body bold mb-8">Search for the fault</p>
  <app-text-search
    name="freeTextSearch"
    searchIconPosition="start"
    [ngModel]="vm.formValue?.['freeTextSearch']"
    [useOrbitSearch]="{
      isFreeTextSearch: true,
      productGroupCode: vm.productGroupCode,
    }"
    (loadingError)="onSearchError()"
  >
    @if (this.vm.errors['freeTextSearch']?.['required'] && vm.formSubmitted) {
      <section errors class="error flex gap-8 mt-8">
        <i class="fa-light fa-circle-exclamation"></i>

        <p class="body" data-testid="fault-details-empty-error">Select an option to continue.</p>
      </section>
    }
  </app-text-search>
} @else {
  <p class="body mt-8">
    If your fault isn’t listed, please pick the most similar. Or if you have more than one fault, pick the main one.
  </p>
  @if (vm.showFaultCategorySelection) {
    <fieldset class="mt-16">
      @if (!faultCategory) {
        <legend class="label mb-8">Step 1: Select a category</legend>

        <section #categoryScrollElement class="scroll-container" data-testid="fault-category-list">
          <section class="p-4" [style.height.px]="categoryVirtualiser?.getTotalSize()">
            @for (option of vm.faultCategory; track option.value; let index = $index) {
              <app-radio-button
                [index]="index"
                [includeResetOption]="false"
                [optionValue]="option.value"
                [ngModel]="faultCategory"
                class="flex mb-8"
                variant="select"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="faultDetails = undefined; $event === undefined ? null : (faultCategory = $event)"
                dataTestId="fault-category"
              >
                <p class="body radio-text">{{ option.title }}</p>
              </app-radio-button>
            }
          </section>
        </section>
      } @else {
        <section class="selected-state flex-col">
          <section class="label mb-8 flex">
            <span>Selected category</span>

            <button class="link-text" type="button" (click)="faultCategory = undefined">
              <span> Remove </span>
              <i class="fa-light fa-trash"></i>
            </button>
          </section>

          <section>
            <i class="fa-light fa-circle-check"></i>

            <p class="body">
              {{ faultCategory.faultCategoryLabel }}
            </p>
          </section>
        </section>
      }
    </fieldset>
  }

  <fieldset [hidden]="!vm.faultDetailsActive" class="mt-16">
    @if (!faultDetails) {
      <legend
        class="label mb-8"
        [attr.data-testid]="
          vm.isHeatingPlan ? 'heating-fault-selection-title-unselected' : 'non-heating-fault-selection-title-unselected'
        "
      >
        @if (vm.isHeatingPlan) {
          Select a fault
        } @else {
          Step {{ vm.faultStep }}: Select a fault
        }
      </legend>

      <section #faultScrollElement class="scroll-container" data-testid="fault-detail-list">
        <section class="p-4" [style.height.px]="faultVirtualiser?.getTotalSize()">
          @for (option of vm.faultDetails; track option.value; let index = $index) {
            <app-radio-button
              [ngModel]="faultDetails"
              [index]="index"
              [includeResetOption]="false"
              [optionValue]="option.value"
              class="flex mb-8"
              variant="select"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="$event === undefined ? null : (faultDetails = $event)"
              dataTestId="fault-detail"
            >
              <p class="body radio-text">{{ option.title }}</p>
            </app-radio-button>
          }
        </section>
      </section>
    } @else {
      <section class="selected-state flex-col">
        <section class="label mb-8 flex">
          <span>Selected fault</span>
          <button class="link-text" type="button" (click)="faultDetails = undefined">
            <span> Remove </span>
            <i class="fa-light fa-trash"></i>
          </button>
        </section>

        <section>
          <i class="fa-light fa-circle-check"></i>

          <p class="body">
            {{ faultDetails.faultLabel }}
          </p>
        </section>
      </section>
    }
  </fieldset>
}

@if (vm.showUsabilityQuestion && !vm.isHeatingPlan) {
  <fieldset class="appliance-usable mt-16">
    <legend class="label">Can you still use the appliance?</legend>

    <section class="options mt-8">
      @for (option of [0, 1]; track option; let index = $index) {
        <app-radio-button
          style="flex: 1"
          variant="blue"
          [ngModel]="vm.formValue?.['applianceUsable']"
          name="applianceUsable"
          [index]="index"
          [includeResetOption]="false"
          [attr.data-TestId]="'appliance-usable' + (index === 0 ? '-yes' : '-no')"
          [optionValue]="index === 0"
        >
          {{ index === 0 ? 'Yes, I can still use it' : "No, I can't use it" }}
        </app-radio-button>
      }
    </section>
  </fieldset>
}

@if ((vm.errors?.faultCategory || vm.errors?.faultDetails || vm.errors?.applianceUsable) && vm.formSubmitted) {
  <section class="error mt-16">
    <i class="fa-light fa-circle-exclamation"></i>
    <p class="body" data-testid="fault-details-empty-error">Select an option to continue.</p>
  </section>
}

@if (
  eligibleForSmartFixEmbeddedContentPanel &&
  (faultDetails || vm.formValue?.['faultDetails'] || vm.formValue?.['freeTextSearch']) &&
  (!vm.showUsabilityQuestion || vm.formValue?.['applianceUsable'] !== undefined)
) {
  <app-smart-fix-embedded-fix #smartFixEmbeddedContent></app-smart-fix-embedded-fix>
}

<div class="divider"></div>
@if (vm.showWarningBox) {
  <section class="warning-box mb-16" data-testid="booking-editability-warning">
    <section class="title">
      <i class="fa-light fa-circle-exclamation fa-lg"></i>
      <p class="label">This can't be changed later, just so you know</p>
    </section>

    <p class="body">
      After this page, you won’t be able to change what you’ve entered so far. So, please make sure you’re happy with
      your selected fault.
    </p>
  </section>
}

<input hidden name="faultCategory" [ngModel]="faultCategory" />
<input hidden name="faultDetails" [ngModel]="faultDetails" />
